import { getDeviceInfo } from '../utils/device.js';
import { setLanguage, clearUserLanguage } from '../i18n.js';
import { setSentryUser, clearSentryUser } from '../sentry.js';

const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const INSTANCE_KEY = 'instance_name';
const USER_EMAIL_KEY = 'user_email';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getInstance() {
  return localStorage.getItem(INSTANCE_KEY);
}

export function getUserEmail() {
  return localStorage.getItem(USER_EMAIL_KEY);
}

export function isAuthenticated() {
  return !!(getToken() && getInstance());
}

export function storeTokens(jwt, refreshToken) {
  localStorage.setItem(TOKEN_KEY, jwt);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(INSTANCE_KEY);
  localStorage.removeItem(USER_EMAIL_KEY);
  clearUserLanguage();
  clearSentryUser();
}

export async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  const instance = getInstance();

  if (!refreshToken || !instance) {
    clearTokens();
    throw new Error('No refresh token available');
  }

  const response = await fetch(`https://${instance}.assistgo.de/api/user/refresh-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    clearTokens();
    throw new Error('Token refresh failed');
  }

  const data = await response.json();
  storeTokens(data.jwt, data.refreshToken);
  return data.jwt;
}

export async function logout() {
  try {
    const token = getToken();
    const refreshToken = getRefreshToken();
    const instance = getInstance();

    if (token && instance) {
      await fetch(`https://${instance}.assistgo.de/api/user/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ refreshToken }),
      });
    }
  } catch (error) {
    console.error('Logout API call failed:', error);
  } finally {
    clearTokens();
  }
}

export async function verifyDevice(email, deviceId, code, instance) {
  try {
    const url = `https://${instance}.assistgo.de/api/devices/verify`;
    console.log('Making device verification request to:', url);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, deviceId, code }),
    });

    if (!response.ok) {
      const errorData = await response.json();

      if (errorData.requiresTwoFactor || errorData.requiresTwoFactorSetup) {
        const twoFactorResult = {
          requiresTwoFactor: !!errorData.requiresTwoFactor,
          requiresTwoFactorSetup: !!errorData.requiresTwoFactorSetup,
          jwt: errorData.jwt,
          instance,
        };
        const err = new Error('Two-factor authentication required');
        err.twoFactor = twoFactorResult;
        throw err;
      }

      throw new Error(errorData.message || 'Device verification failed');
    }

    const data = await response.json();

    if (data.requiresTwoFactor || data.requiresTwoFactorSetup) {
      const err = new Error('Two-factor authentication required');
      err.twoFactor = {
        requiresTwoFactor: !!data.requiresTwoFactor,
        requiresTwoFactorSetup: !!data.requiresTwoFactorSetup,
        jwt: data.jwt,
        instance,
      };
      throw err;
    }

    if (!data.jwt) {
      throw new Error('No token received from server');
    }
    
    storeTokens(data.jwt, data.refreshToken);
    localStorage.setItem(USER_EMAIL_KEY, email);
    localStorage.setItem(INSTANCE_KEY, instance);
    setSentryUser({ email, instance });

    const lang = data.user?.language || data.language;
    if (lang) {
      setLanguage(lang);
    }

    return data;
  } catch (error) {
    if (error.twoFactor) throw error;
    console.error('Device verification error:', error);
    throw new Error(error.message || 'Device verification failed');
  }
}

export async function login(email, password, instance) {
  console.log('Login called with instance:', instance);
  console.log('Instance type:', typeof instance);

  if (!instance) {
    console.error('Instance is missing or empty');
    throw new Error('Instance name is required');
  }

  try {
    // Store instance name first
    console.log('Storing instance:', instance);
    localStorage.setItem(INSTANCE_KEY, instance);
    
    // Make API call to login endpoint
    const url = `https://${instance}.assistgo.de/api/user/login`;
    console.log('Making request to:', url);
    
    // Get device information (using basic detection to avoid loading large library)
    const device = await getDeviceInfo(false); // false = use lightweight detection
    console.log('Device info:', device);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password, device }),
    });

    if (!response.ok) {
      localStorage.removeItem(INSTANCE_KEY);
      
      const errorData = await response.json();

      if (errorData.requiresTwoFactor || errorData.requiresTwoFactorSetup) {
        const twoFactorError = new Error('Two-factor authentication required');
        twoFactorError.twoFactor = {
          requiresTwoFactor: !!errorData.requiresTwoFactor,
          requiresTwoFactorSetup: !!errorData.requiresTwoFactorSetup,
          jwt: errorData.jwt,
          instance,
        };
        throw twoFactorError;
      }

      if (errorData.statusCode === 401 && errorData.keys && errorData.keys.includes('device.isVerified')) {
        const verificationError = new Error(errorData.message || 'Device verification required');
        verificationError.requiresDeviceVerification = true;
        verificationError.deviceId = device.id;
        verificationError.email = email;
        verificationError.instance = instance;
        throw verificationError;
      }
      
      throw new Error(errorData.message || 'Invalid credentials');
    }

    const data = await response.json();

    if (data.requiresTwoFactorSetup || data.requiresTwoFactor) {
      localStorage.removeItem(INSTANCE_KEY);
      const twoFactorError = new Error('Two-factor authentication required');
      twoFactorError.twoFactor = {
        requiresTwoFactor: !!data.requiresTwoFactor,
        requiresTwoFactorSetup: !!data.requiresTwoFactorSetup,
        jwt: data.jwt,
        instance,
      };
      throw twoFactorError;
    }

    if (!data.jwt) {
      throw new Error('No token received from server');
    }
    
    storeTokens(data.jwt, data.refreshToken);
    localStorage.setItem(USER_EMAIL_KEY, email);
    setSentryUser({ email, instance });

    const lang = data.user?.language || data.language;
    if (lang) {
      setLanguage(lang);
    }

    return data;
  } catch (error) {
    if (!error.requiresDeviceVerification && !error.twoFactor) {
      clearTokens();
    }
    console.error('Login error:', error);
    
    throw error;
  }
}

export async function twoFactorSetup(tempToken, instance) {
  const url = `https://${instance}.assistgo.de/api/user/2fa/setup`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tempToken}`,
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to initialize 2FA setup');
  }

  return response.json();
}

export async function twoFactorEnable(tempToken, instance, code) {
  const url = `https://${instance}.assistgo.de/api/user/2fa/enable`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tempToken}`,
    },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Invalid verification code');
  }

  return response.json();
}

export async function twoFactorVerify(tempToken, instance, payload) {
  const url = `https://${instance}.assistgo.de/api/user/2fa/verify`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tempToken}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Verification failed');
  }

  const body = await response.json();

  if (body.jwt && body.email) {
    return { success: true, data: body };
  }
  if (body.success && body.data) {
    return body;
  }

  throw new Error(body.error || 'Verification failed');
}

export function completeLoginAfterTwoFactor(userData, instance) {
  const data = userData.data || userData;
  const jwt = data.jwt || data.token || data.accessToken;

  if (!jwt) {
    throw new Error('No token received after 2FA verification');
  }

  storeTokens(jwt, data.refreshToken);
  localStorage.setItem(INSTANCE_KEY, instance);

  if (data.email) {
    localStorage.setItem(USER_EMAIL_KEY, data.email);
  }

  setSentryUser({
    email: data.email || localStorage.getItem(USER_EMAIL_KEY),
    instance,
  });

  const lang = data.user?.language || data.language;
  if (lang) {
    setLanguage(lang);
  }
}

